import React, { useEffect, useState } from 'react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import ResyncProgress from './ResyncProgress';
import '../shared/ConfirmModal.css';

// BULK SELECTED RESYNC — resync the rows the user ticked on Pipelines › Logs, which may span several hotels. The server
// resolves each selected row to its records (one for a single message, every failed record for a batch), groups them by
// implementation + kind, and starts one resync run per group. One run per (implementation, kind) is the same OHIP
// Adapter resync the single-pipeline ResyncModal starts — so there is no new behaviour, only the selection resolved and
// started together. The shared ResyncProgress then shows per-hotel progress: hotel · kind · record count · live status.
//
// `items`: [{ groupId, batch }] — the selected rows. Superadmin only (the button is hidden too).

export default function BulkSelectedResyncModal({ items, onClose }) {
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [runs, setRuns] = useState(null);   // null before start; after: the server's runs

    const start = async () => {
        setError(null);
        setStarting(true);
        try {
            const res = await integrationsService.resyncLogSelected({ items });
            const started = res.runs || [];
            setRuns(started);
            setStarting(false);
            if (!started.length) setError('None of the selected rows had a re-queryable record.');
        } catch (e) {
            setStarting(false);
            setError(apiErrorMessage(e, 'The resync could not be started.'));
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const n = items.length;

    return (
        <div className="cm-backdrop" onClick={onClose}>
            <div className="cm-dialog cm-dialog-wide" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <h2 className="cm-title">Resync {n} selected record{n === 1 ? '' : 's'}</h2>

                {!runs ? (
                    <>
                        <p className="cm-message">
                            Each selected row is re-queried from OHIP by its id and sent through the pipeline again, to
                            live targets — a batch row resyncs every record it failed on. Everything is read fresh from
                            OHIP (not a replay of what arrived before) and runs as its own “Resync” record. This can span
                            several hotels; one resync run is started per implementation and kind.
                        </p>

                        {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={onClose}>Cancel</button>
                            <button className="cm-btn-confirm" onClick={start} disabled={starting}>
                                {starting ? 'Starting…' : `Resync ${n} selected`}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <ResyncProgress runs={runs} noun="resyncs" />

                        {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={onClose}>Close</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
